// Officiating summary tile for UMPIRE-role users on the Home
// Dashboard. Shows a lean summary (count of open polls + confirmed
// matches) and opens the full poll list in a portalled modal on
// demand — the dashboard itself stays clean.
//
// Data flow: fetches once on mount via api.listMyPolls(). Nominate /
// withdraw actions happen inside the modal and refresh the same
// hook's state, so the count on the dashboard updates the moment the
// modal closes.

import React, { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import {
  Gavel, MapPin, Clock, CheckCircle2, Ban, ChevronRight, Loader2, Trophy,
  X, Sparkles
} from "lucide-react";
import { useToast } from "./Toast";
import { api } from "../api";
import { fmtMatchDate, fmt12 } from "../views/league/shared";

export default function OfficiatingPollsCard({ navigate }) {
  const toast = useToast();
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [err,     setErr]     = useState("");
  const [open,    setOpen]    = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const d = await api.listMyPolls();
      setData(d);
    } catch (e) {
      setErr(e?.message || "Couldn't load polls.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Body-scroll lock + Escape while the modal is open.
  useEffect(() => {
    if (!open) return;
    const onKey = e => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open]);

  const polls    = data?.polls || [];
  const openPolls  = polls.filter(p => p.pollOpen);
  const assigned   = polls.filter(p => p.myStatus === "Assigned");
  const openCount  = openPolls.length;
  const assignedCount = assigned.length;

  // Summary line — adapts to what the user actually has.
  let summaryLine;
  if (loading)                                summaryLine = "Loading polls…";
  else if (err)                               summaryLine = "Couldn't load polls";
  else if (openCount === 0 && assignedCount === 0) summaryLine = "No open polls";
  else if (openCount === 0)                   summaryLine = `${assignedCount} confirmed match${assignedCount === 1 ? "" : "es"}`;
  else                                        summaryLine = `${openCount} open poll${openCount === 1 ? "" : "s"}${assignedCount ? ` · ${assignedCount} confirmed` : ""}`;

  return (
    <>
      <button
        type="button"
        className="officiatingTile"
        onClick={() => setOpen(true)}
        disabled={loading}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <span className="officiatingTile-ico"><Gavel size={22}/></span>
        <span className="officiatingTile-body">
          <small>OFFICIATING</small>
          <strong>{summaryLine}</strong>
        </span>
        <span className="officiatingTile-cta">
          {loading ? <Loader2 size={14} className="officiatingTile-spin"/> : <>Open <ChevronRight size={14}/></>}
        </span>
      </button>

      {open && createPortal(
        <OfficiatingPollsModal
          data={data}
          loading={loading}
          err={err}
          onClose={() => setOpen(false)}
          onRefresh={refresh}
          toast={toast}
          navigate={navigate}
        />,
        document.body
      )}
    </>
  );
}

function OfficiatingPollsModal({ data, loading, err, onClose, onRefresh, toast, navigate }) {
  const [busyId, setBusyId] = useState(null);

  const polls    = data?.polls || [];
  const openAll  = polls.filter(p => p.pollOpen);
  const assigned = polls.filter(p => p.myStatus === "Assigned");
  const firstName = data?.umpire?.firstName || "";

  async function nominate(matchId) {
    setBusyId(matchId);
    try {
      await api.selfNominate(matchId);
      toast.success("You're on the poll — good luck!");
      await onRefresh();
    } catch (e) {
      toast.error(e?.message || "Couldn't submit your response.");
    } finally { setBusyId(null); }
  }

  async function withdraw(matchId) {
    if (!confirm("Withdraw your availability for this match?")) return;
    setBusyId(matchId);
    try {
      await api.withdrawNomination(matchId);
      toast.success("Withdrawn.");
      await onRefresh();
    } catch (e) {
      toast.error(e?.message || "Couldn't withdraw.");
    } finally { setBusyId(null); }
  }

  return (
    <div className="officiatingModal" role="dialog" aria-modal="true" aria-labelledby="officiatingModal-title">
      <div className="officiatingModal-scrim" onClick={onClose}/>
      <div className="officiatingModal-body">
        <header className="officiatingModal-head">
          <div>
            <p>Umpire</p>
            <h2 id="officiatingModal-title">
              My Polls{firstName ? ` · ${firstName}` : ""}
            </h2>
          </div>
          <button
            type="button"
            className="officiatingModal-close"
            onClick={onClose}
            aria-label="Close"
          ><X size={18}/></button>
        </header>

        <div className="officiatingModal-content">
          {loading && (
            <p style={{color:"var(--muted)", textAlign:"center", padding:"24px"}}>
              Loading polls…
            </p>
          )}

          {err && <div className="officiatingModal-err">{err}</div>}

          {!loading && !err && assigned.length > 0 && (
            <section>
              <h3 className="officiatingModal-sectionTitle">
                <Sparkles size={14}/> Confirmed matches
              </h3>
              <div className="pollCard-grid">
                {assigned.map(p => (
                  <PollCard
                    key={p.id}
                    poll={p}
                    variant="assigned"
                  />
                ))}
              </div>
            </section>
          )}

          {!loading && !err && (
            <section>
              <h3 className="officiatingModal-sectionTitle">
                <Gavel size={14}/> Open polls · nominate yourself
              </h3>
              {openAll.length === 0 ? (
                <div className="officiatingModal-empty">
                  <Gavel size={28}/>
                  <p>No open polls right now. When the committee opens one, it'll show up here.</p>
                </div>
              ) : (
                <div className="pollCard-grid">
                  {openAll.map(p => (
                    <PollCard
                      key={p.id}
                      poll={p}
                      variant={p.myStatus === "Nominated" ? "nominated" : "open"}
                      busy={busyId === p.id}
                      onNominate={() => nominate(p.id)}
                      onWithdraw={() => withdraw(p.id)}
                    />
                  ))}
                </div>
              )}
            </section>
          )}
        </div>

        <footer className="officiatingModal-foot">
          <button
            type="button"
            className="officiatingCard-more"
            onClick={() => { onClose(); navigate?.("umpire-polls"); }}
          >
            Open full page →
          </button>
          <button
            type="button"
            className="officiatingCard-btn officiatingCard-btn--withdraw"
            onClick={onClose}
            style={{ marginLeft: "auto" }}
          >
            Back to dashboard
          </button>
        </footer>
      </div>
    </div>
  );
}

// Full poll card — vertical, self-contained. Header shows date/time
// and status pill; body shows the teams as a "vs" hero + venue meta;
// footer shows nomination counts + the primary action.
function PollCard({ poll, variant, busy, onNominate, onWithdraw }) {
  const assigned  = variant === "assigned";
  const nominated = variant === "nominated";
  return (
    <article className={`pollCard pollCard--${variant}`}>
      <header className="pollCard-head">
        <div className="pollCard-date">
          <b>{fmtMatchDate(poll.date)}</b>
          {poll.time && <small><Clock size={10}/> {fmt12(poll.time)} ET</small>}
        </div>
        <span className={`pollCard-status pollCard-status--${variant}`}>
          {assigned  && <><CheckCircle2 size={11}/> Officiating</>}
          {nominated && <><CheckCircle2 size={11}/> Nominated</>}
          {variant === "open" && <>Open</>}
        </span>
      </header>

      <div className="pollCard-vs">
        <span className="pollCard-team">{poll.teamOne}</span>
        <span className="pollCard-vsLabel">vs</span>
        <span className="pollCard-team">{poll.teamTwo}</span>
      </div>

      <div className="pollCard-meta">
        <span><MapPin size={12}/> {poll.ground}</span>
        {poll.series && <span><Trophy size={12}/> {poll.series}</span>}
      </div>

      <footer className="pollCard-foot">
        <div className="pollCard-counts">
          <div>
            <b>{poll.nominatedCount}</b>
            <small>Nominated</small>
          </div>
          <div>
            <b>{poll.assignedCount}<em>/2</em></b>
            <small>Assigned</small>
          </div>
        </div>
        {!assigned && (
          nominated ? (
            <button
              type="button"
              className="pollCard-btn pollCard-btn--withdraw"
              onClick={onWithdraw}
              disabled={busy}
            >
              {busy
                ? <><Loader2 size={12} className="officiatingCard-spin"/> Working…</>
                : <><Ban size={12}/> Withdraw</>}
            </button>
          ) : (
            <button
              type="button"
              className="pollCard-btn pollCard-btn--in"
              onClick={onNominate}
              disabled={busy}
            >
              {busy
                ? <><Loader2 size={12} className="officiatingCard-spin"/> Submitting…</>
                : <><CheckCircle2 size={12}/> I'm available</>}
            </button>
          )
        )}
      </footer>
    </article>
  );
}
